import { resolveProjectBreadcrumbRoute } from "./projectBreadcrumbRoute";
/**
 * Route → breadcrumb resolution, kept pure and dependency-free.
 *
 * The global bar renders one breadcrumb for every surface, so the answer has to
 * exist before any surface-specific data arrives. This module is that floor: it
 * maps a pathname to the crumbs that can be known from the URL alone.
 *
 * Surfaces that own a human title the URL cannot carry (a thread name, a
 * project name) resolve it separately and override the leaf — see
 * `useGlobalBreadcrumbCrumbs`. Keeping the two apart is what stops the bar from
 * flashing empty while a title loads: the static answer renders immediately and
 * the richer one replaces it in place.
 */

/**
 * Identity artwork for a crumb, as plain data.
 *
 * Deliberately not a `ReactNode`: this module stays pure and testable, and the
 * renderer decides how a project mark is drawn.
 *
 * The fields mirror the shared `ProjectIcon` ladder rather than one provenance,
 * because the bar names projects reached three different ways. A CMMD project
 * carries only `canonicalIdentity`; a Forge project reached through a runtime
 * also carries the checkout coordinates, so a plain local project still gets
 * its own favicon instead of a letter avatar.
 */
export interface BreadcrumbCrumbIcon {
  /** Used for the letter fallback, and as the image's accessible owner. */
  readonly name: string;
  /** CMMD-owned identity, when CMMD owns this project's identity. */
  readonly canonicalIdentity?:
    | { readonly iconUrl: string | null; readonly icon: string | null }
    | undefined;
  /** Checkout coordinates for the favicon fallback, when the project has one. */
  readonly environmentId?: string | undefined;
  readonly cwd?: string | undefined;
}

export interface BreadcrumbCrumb {
  /** Stable list key — path-derived, so it survives a label swap. */
  readonly key: string;
  readonly label: string;
  /** Present on ancestor crumbs only; the leaf is never a link. */
  readonly to?: string;
  readonly icon?: BreadcrumbCrumbIcon;
}

/** Leaf label used until a surface publishes the real thread title. */
export const THREAD_FALLBACK_LABEL = "Thread";
/** Leaf label used until a surface publishes the real project name. */
export const PROJECT_FALLBACK_LABEL = "Project";
/** Leaf label used until a surface publishes the real automation name. */
export const AUTOMATION_FALLBACK_LABEL = "Automation";
// The global Projects overview was removed, so this ancestor crumb has
// nowhere to link to anymore — it is label-only context for project routes.
const AUTOMATIONS_CRUMB: BreadcrumbCrumb = {
  key: "automations",
  label: "Automations",
  to: "/automations",
};
const PLUGINS_CRUMB: BreadcrumbCrumb = { key: "plugins", label: "Plugins", to: "/plugins" };
const PULL_REQUESTS_CRUMB: BreadcrumbCrumb = {
  key: "pull-requests",
  label: "Pull requests",
};
const SETTINGS_CRUMB: BreadcrumbCrumb = { key: "settings", label: "Settings", to: "/settings" };

/**
 * Turns an unrecognised URL segment into something a person can read, so a route
 * added after this module still gets a sane crumb instead of a raw slug.
 */
export function humanizeSegment(segment: string): string {
  const spaced = decodeSegment(segment).replace(/[-_]+/g, " ").trim();
  if (spaced.length === 0) {
    return segment;
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    // A malformed escape is still better shown raw than crashing the bar.
    return segment;
  }
}

export function splitPathname(pathname: string): readonly string[] {
  return pathname.split("/").filter((segment) => segment.length > 0);
}

/**
 * The crumbs derivable from the URL alone. Never returns an empty array — an
 * empty breadcrumb reads as a broken bar, so unknown routes fall back to
 * humanised segments.
 */
/**
 * `/project/<cmmdProjectId>` is the canonical project page. `/cmmd/project/` is
 * still recognised because it is a redirect shim for links already written into
 * CMMD task cards, and the crumb has to be right for the frame that renders
 * while the redirect resolves.
 */

export function resolveStaticBreadcrumb(
  pathname: string,
  settingsLabelByPath?: ReadonlyMap<string, string>,
): readonly BreadcrumbCrumb[] {
  const segments = splitPathname(pathname);

  if (segments.length === 0) {
    return [{ key: "new", label: "New thread" }];
  }

  const [first, second, third] = segments;

  if (first === "new") {
    return [{ key: "new", label: "New thread" }];
  }

  // Keyed "thread", not "new": a draft is a thread being composed, and the chat
  // surface publishes its title against that key once the draft has one. Give
  // it a different key and the draft's crumb reads "New thread" forever, even
  // while the page itself shows the real name.
  if (first === "draft") {
    return [{ key: "thread", label: "New thread" }];
  }

  if (first === "setup") {
    return [{ key: "setup", label: "Setup" }];
  }

  if (first === "settings") {
    return resolveSettingsBreadcrumb(segments, settingsLabelByPath);
  }

  const projectRoute = resolveProjectBreadcrumbRoute(pathname);
  if (projectRoute !== null) {
    return projectRouteCrumbs(projectRoute);
  }

  if (first === "automations") {
    return resolveAutomationsBreadcrumb(second, third);
  }

  if (first === "plugins") {
    if (second === undefined) {
      return [{ key: "plugins", label: "Plugins" }];
    }
    return [PLUGINS_CRUMB, { key: "plugin-detail", label: humanizeSegment(second) }];
  }
  if (first === "pull-requests") return [PULL_REQUESTS_CRUMB];

  // Connected subscriptions already rolls every account's usage together, so
  // the page needs only the one crumb. Estimated value is reached from a
  // button on the page, not from a second breadcrumb segment.
  if (first === "usage" && second === undefined) {
    return [{ key: "usage", label: "Usage" }];
  }

  // Whatever is left with exactly two segments is the thread route
  // (`/$environmentId/$threadId`). It is checked last precisely because it has
  // no literal prefix to match on and would otherwise swallow the routes above.
  if (segments.length === 2) {
    return [{ key: "thread", label: THREAD_FALLBACK_LABEL }];
  }

  return segments.map((segment, index) => ({
    key: segments.slice(0, index + 1).join("/"),
    label: humanizeSegment(segment),
  }));
}

function resolveSettingsBreadcrumb(
  segments: readonly string[],
  settingsLabelByPath?: ReadonlyMap<string, string>,
): readonly BreadcrumbCrumb[] {
  const section = segments[1];
  if (section === undefined) {
    return [{ key: "settings", label: "Settings" }];
  }

  const path = `/settings/${section}`;
  // The settings nav is the single source of truth for these labels, so a
  // renamed section renames here too. Legacy sections that never appear in the
  // nav still get a readable crumb rather than nothing.
  const label = settingsLabelByPath?.get(path) ?? humanizeSegment(section);
  return [SETTINGS_CRUMB, { key: path, label }];
}

function resolveAutomationsBreadcrumb(
  second: string | undefined,
  third: string | undefined,
): readonly BreadcrumbCrumb[] {
  if (second === undefined) {
    return [{ key: "automations", label: "Automations" }];
  }

  if (second === "new") {
    return [AUTOMATIONS_CRUMB, { key: "automation-new", label: "New automation" }];
  }

  const detail: BreadcrumbCrumb = {
    key: "automation-detail",
    label: AUTOMATION_FALLBACK_LABEL,
  };

  if (third === "edit") {
    return [
      AUTOMATIONS_CRUMB,
      { ...detail, to: `/automations/${second}` },
      { key: "automation-edit", label: "Edit" },
    ];
  }

  return [AUTOMATIONS_CRUMB, detail];
}

/**
 * Replaces the trailing crumb's label once a surface knows the real name,
 * leaving ancestors and links untouched. Returns the input unchanged when the
 * title is absent, so a still-loading surface keeps its fallback.
 */
export function withResolvedLeafLabel(
  crumbs: readonly BreadcrumbCrumb[],
  label: string | null | undefined,
): readonly BreadcrumbCrumb[] {
  const leaf = crumbs[crumbs.length - 1];
  return leaf === undefined ? crumbs : withResolvedLabel(crumbs, leaf.key, label);
}

/**
 * Replaces one crumb's label by key. Unlike the leaf-only form this can name an
 * ancestor, which `/automations/:id/edit` needs — its middle crumb is the
 * automation, and the leaf is the word "Edit".
 */
export function withResolvedLabel(
  crumbs: readonly BreadcrumbCrumb[],
  key: string,
  label: string | null | undefined,
): readonly BreadcrumbCrumb[] {
  const trimmed = label?.trim();
  if (trimmed === undefined || trimmed.length === 0) {
    return crumbs;
  }

  let replaced = false;
  const next = crumbs.map((crumb) => {
    if (crumb.key !== key) {
      return crumb;
    }
    replaced = true;
    return { ...crumb, label: trimmed };
  });

  return replaced ? next : crumbs;
}

/**
 * Inserts a linkable ancestor in front of the existing crumbs — used when a
 * surface discovers its parent (a thread's project, say) after the fact.
 */
export function withParentCrumb(
  crumbs: readonly BreadcrumbCrumb[],
  parent: BreadcrumbCrumb | null,
): readonly BreadcrumbCrumb[] {
  if (parent === null) {
    return crumbs;
  }
  return [parent, ...crumbs];
}

function projectRouteCrumbs(
  route: NonNullable<ReturnType<typeof resolveProjectBreadcrumbRoute>>,
): readonly BreadcrumbCrumb[] {
  const project: BreadcrumbCrumb = { key: "project-detail", label: PROJECT_FALLBACK_LABEL };
  return route.section === null
    ? [project]
    : [
        { ...project, to: route.projectTo },
        { key: "project-tasks", label: route.section },
      ];
}
